import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from './api.service';
import { I18nService, TranslatePipe } from './i18n.service';

@Component({
  selector: 'app-inventory',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="pb-24 lg:pb-8 min-h-screen lg:min-h-[calc(100vh-72px)] flex flex-col" style="background:#F7FBFD;">
      <header class="px-5 pt-4 pb-4 sticky top-0 z-30 text-white shadow-sm flex items-center gap-3 lg:hidden"
              style="background:linear-gradient(135deg,#0F334A 0%,#145B7A 100%);">
        @if (selectedParent()) {
          <button (click)="goBack()" class="w-10 h-10 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center text-white/90 active:bg-white/15 shrink-0">
            <i class="ti ti-arrow-left text-[20px]"></i>
          </button>
        }
        <div class="min-w-0 flex-1">
          <p class="text-[10px] font-medium text-white/55 uppercase tracking-widest">
            {{ selectedParent() ? 'Category' : ('inventory' | t) }}
          </p>
          <h1 class="text-lg font-semibold text-white/95 truncate">
            {{ selectedParent() ? selectedParent().name : ('materials' | t) }}
          </h1>
        </div>
        <span class="text-[10px] font-medium px-2.5 py-1 rounded-full uppercase border bg-white/10 text-white/75 border-white/15">
          {{ materials().length }}
        </span>
      </header>

      <main class="px-4 pt-5 flex-1 space-y-4 lg:px-7 lg:pt-6">
        @if (selectedParent()) {
          <section class="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3 flex items-center gap-3">
            <div class="w-9 h-9 rounded-xl bg-white text-[#1A6FA6] border border-sky-100 flex items-center justify-center shrink-0">
              <i class="ti ti-category text-lg"></i>
            </div>
            <div class="min-w-0">
              <p class="text-[10px] font-semibold text-sky-500 uppercase tracking-wider">Browsing</p>
              <p class="text-sm font-semibold text-sky-800 truncate">{{ selectedParent().name }}</p>
            </div>
          </section>
        }

        <section class="relative">
          <i class="ti ti-search absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-lg"></i>
          <input type="text"
                 [placeholder]="'search' | t"
                 [(ngModel)]="searchQuery"
                 (input)="onSearch()"
                 class="w-full h-11 pl-11 pr-11 bg-white border border-slate-100 rounded-2xl font-normal focus:outline-none focus:border-[#1A6FA6] focus:ring-2 focus:ring-sky-100 transition-all text-sm text-slate-800">
          @if (searchQuery.trim()) {
            <button (click)="clearSearch()" class="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-xl bg-slate-50 text-slate-400 flex items-center justify-center active:bg-slate-100">
              <i class="ti ti-x"></i>
            </button>
          }
        </section>

        @if (loadError()) {
          <section class="bg-rose-50 border border-rose-100 rounded-2xl p-4 flex items-start gap-3">
            <i class="ti ti-alert-circle text-rose-500 text-xl shrink-0"></i>
            <div class="flex-1">
              <p class="text-sm font-semibold text-rose-800">Unable to load materials</p>
              <p class="text-xs text-rose-600 mt-0.5">{{ loadError() }}</p>
            </div>
            <button (click)="loadMaterials()" class="text-xs font-semibold text-rose-700 bg-white/70 border border-rose-100 rounded-lg px-3 py-1.5">
              {{ 'retry' | t }}
            </button>
          </section>
        }

        @if (isLoading()) {
          <section class="space-y-3">
            @for (n of [1,2,3,4,5]; track n) {
              <div class="bg-white rounded-2xl p-4 flex gap-3 items-center animate-pulse border border-slate-100">
                <div class="w-12 h-12 rounded-xl bg-slate-100 shrink-0"></div>
                <div class="flex-1 space-y-2">
                  <div class="h-3 bg-slate-100 rounded-full w-1/3"></div>
                  <div class="h-4 bg-slate-100 rounded-full w-3/4"></div>
                  <div class="h-3 bg-slate-100 rounded-full w-1/2"></div>
                </div>
              </div>
            }
          </section>
        } @else {
          <section>
            <div class="flex items-center justify-between mb-2 px-1">
              <p class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{{ listTitle() }}</p>
              @if (searchQuery.trim()) {
                <span class="text-[11px] font-medium text-slate-400">{{ searchQuery.trim() }}</span>
              }
            </div>

            @if (materials().length === 0) {
              <div class="flex flex-col items-center justify-center py-16 text-center bg-white rounded-2xl border border-slate-100">
                <div class="w-14 h-14 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center mb-4">
                  <i class="ti ti-package-off text-2xl text-slate-300"></i>
                </div>
                <h3 class="font-semibold text-slate-700 text-base mb-1">{{ 'noItemsFound' | t }}</h3>
                <p class="text-slate-400 text-sm max-w-xs leading-relaxed">Try a different material name or serial number.</p>
              </div>
            } @else {
              <div class="space-y-3 lg:grid lg:grid-cols-2 xl:grid-cols-3 lg:gap-3 lg:space-y-0">
                @for (item of materials(); track item.id) {
                  <button type="button"
                          (click)="onItemClick(item)"
                          class="w-full bg-white rounded-2xl p-4 border border-slate-100 flex items-center gap-3 transition-all active:scale-[0.98] text-left"
                          [class.cursor-default]="!hasChildren(item)">
                    <div class="w-12 h-12 rounded-xl border border-slate-100 overflow-hidden bg-slate-50 shrink-0 flex items-center justify-center">
                      <img [src]="item.photo_url || defaultPhoto" (error)="onImageError($event)" class="w-full h-full object-cover" alt="">
                    </div>

                    <div class="flex-1 min-w-0">
                      <div class="flex items-center gap-2 mb-0.5">
                        <span class="text-[9px] font-medium text-slate-500 bg-slate-50 px-2 py-0.5 rounded-full uppercase border border-slate-100">{{ item.unit || 'EA' }}</span>
                        <span class="text-[9px] font-medium text-[#1A6FA6] font-mono truncate">{{ item.serial_no }}</span>
                      </div>
                      <h3 class="font-semibold text-sm text-slate-800 leading-tight truncate">{{ item.name }}</h3>
                      <p class="text-xs text-slate-500 truncate mt-0.5">{{ item.description || 'No description provided' }}</p>
                    </div>

                    <div class="shrink-0 flex flex-col items-end min-w-[72px]">
                      @if (hasChildren(item)) {
                        <div class="w-8 h-8 rounded-xl bg-sky-50 text-[#1A6FA6] border border-sky-100 flex items-center justify-center">
                          <i class="ti ti-chevron-right text-lg"></i>
                        </div>
                        <span class="text-[9px] font-medium text-sky-700 uppercase mt-1">{{ item.sub_item_count }} types</span>
                      } @else {
                        <div class="bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-100">
                          <span class="text-xs font-semibold text-emerald-700">{{ item.max_sell_price | currency:'NZD':'symbol':'1.2-2' }}</span>
                        </div>
                        <span class="text-[9px] font-medium text-slate-400 uppercase mt-1">Unit price</span>
                      }
                    </div>
                  </button>
                }
              </div>
            }
          </section>
        }
      </main>
    </div>
  `
})
export class InventoryComponent implements OnInit, OnDestroy {
  api = inject(ApiService);
  i18n = inject(I18nService);

  materials = signal<any[]>([]);
  isLoading = signal(true);
  loadError = signal('');
  selectedParent = signal<any>(null);
  searchQuery = '';
  defaultPhoto = 'https://nzplumbinghq.co.nz/api/assets/default_mat.jpeg';

  private searchTimer: ReturnType<typeof setTimeout> | undefined;

  listTitle = computed(() => {
    if (this.searchQuery.trim()) return this.i18n.translate('search');
    if (this.selectedParent()) return this.i18n.translate('materials');
    return this.i18n.translate('materials');
  });

  ngOnInit() {
    this.loadMaterials();
  }

  ngOnDestroy() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
  }

  loadMaterials() {
    this.isLoading.set(true);
    this.loadError.set('');

    const term = this.searchQuery.trim();
    const params: any = {
      limit: 50,
      include_children: false,
    };
    if (term) params.search = term;
    if (this.selectedParent()) params.parent_id = this.selectedParent().id;

    this.api.getMaterials(params).subscribe({
      next: (res: any) => {
        this.isLoading.set(false);
        if (res.success) {
          this.materials.set(res.data?.materials || []);
        } else {
          this.materials.set([]);
          this.loadError.set(res.message || 'Materials could not be loaded.');
        }
      },
      error: (err: any) => {
        this.isLoading.set(false);
        this.materials.set([]);
        this.loadError.set(err?.error?.message || 'Materials could not be loaded.');
      }
    });
  }

  onSearch() {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.loadMaterials(), 300);
  }

  clearSearch() {
    this.searchQuery = '';
    this.loadMaterials();
  }

  onItemClick(item: any) {
    if (!this.hasChildren(item)) return;
    this.selectedParent.set(item);
    this.searchQuery = '';
    this.loadMaterials();
  }

  goBack() {
    this.selectedParent.set(null);
    this.searchQuery = '';
    this.loadMaterials();
  }

  hasChildren(item: any): boolean {
    return Number(item?.sub_item_count || 0) > 0;
  }

  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    if (img.src !== this.defaultPhoto) {
      img.src = this.defaultPhoto;
    }
  }
}
